/**
 * feat-227 — bar lane geometry for the Pairing Challenge preview.
 *
 * WHY THESE LIVE OUTSIDE THE COMPONENT: jsdom's CSS parser silently DISCARDS both
 * `calc(...)` widths and `linear-gradient(...)` backgrounds — the declaration is not
 * stored at all, so `element.style.width` reads `""` no matter what was set. A Jest
 * assertion on the rendered width is therefore not merely awkward, it is impossible:
 * a correct value and a badly broken one are indistinguishable at 0×0 with an empty
 * style. Building the strings here makes the part that CAN be wrong — the arithmetic
 * and the clamping — pure, exact, and testable, and leaves only "does the browser
 * honor this CSS" to the browser, where it is checked for AC9.
 *
 * Values are logical dp → px 1:1 in the preview, mirroring the Flutter card.
 */

export const TRACK_HEIGHT = 14;

/**
 * D10 — bare art, larger than the retired 42 dp circle badge, and far taller than
 * the 14 dp track so it overflows ABOVE the bar. Nothing between the mascot and the
 * card body may set `overflow: hidden`.
 */
export const MASCOT_SIZE = 48;

/** Lifts the mascot's center above the track's, so most of the overflow goes up. */
export const MASCOT_RISE = 2;

/**
 * Sized to FULLY CONTAIN the mascot rather than let it bleed into the row above.
 *
 * This is a deliberate divergence from the mockup, agreed with the Flutter slice
 * (2026-08-08) so the two renders share a row rhythm at the AC9 side-by-side. The
 * mockup lets the art bleed up into the label row's whitespace for a tighter pitch;
 * both slices instead reserve the space, because a clipped mascot passes every
 * widget test and only appears on a device (Trap 4). It costs ~14 dp of card height
 * per row and buys a failure mode that cannot hide.
 *
 * The mascot still overflows the BAR — which is what D10 asks for — it just doesn't
 * overflow its lane.
 */
export const LANE_HEIGHT = 52;

/**
 * Reserved at the right of every lane so the 🏁 sits OUTSIDE the track's 100% end
 * instead of on top of it. Request §4.3's layout note: the track used to span the
 * full available width, and left as-is the flag would overlap it or overflow the
 * card. The track is shortened by exactly this much.
 */
export const FLAG_WIDTH = 18;
export const FLAG_GAP = 8;
export const FLAG_LANE = FLAG_WIDTH + FLAG_GAP;

export const CARD_PADDING = 20;
export const LABEL_TO_LANE_GAP = 8;
export const ROW_GAP = 16;

/** Type scale, mirrored from the Flutter slice's constants (2026-08-08). */
export const HEADING_FONT_SIZE = 20;
export const HEADING_BOTTOM_GAP = 20;
export const DOT_SIZE = 8;
export const DOT_GAP = 8;
export const NAME_FONT_SIZE = 15;
export const YOU_FONT_SIZE = 12;
export const YOU_GAP = 6;
export const PERCENT_FONT_SIZE = 26;
export const PERCENT_SUFFIX_FONT_SIZE = 14;
export const FLAG_FONT_SIZE = 14;

/** Vertical offset of the track within its lane. */
export const TRACK_TOP = (LANE_HEIGHT - TRACK_HEIGHT) / 2;

/**
 * Vertical offset of the mascot within its lane. Sits ABOVE `TRACK_TOP` — that gap
 * is D10's "overflowing above the bar", and it is the one thing about the mascot
 * that a jsdom test can actually check, since rects there are all 0×0.
 */
export const MASCOT_TOP = (LANE_HEIGHT - MASCOT_SIZE) / 2 - MASCOT_RISE;

/**
 * Clamp for LAYOUT GEOMETRY ONLY (ADR 0024 D4). The percent LABEL is never passed
 * through here — a stored 104 prints "104%" and a stored -5 prints "-5%". This
 * exists so the fill cannot draw past the track or backwards out of it.
 */
export const clampPct = (n: number): number => Math.max(0, Math.min(100, n));

/** The track: full width less the flag's reserved column. */
export function trackWidth(): string {
  return `calc(100% - ${FLAG_LANE}px)`;
}

/**
 * The fill: `fraction` of the TRACK's width, not of the lane's — so the fill head
 * lands where the track's 100% is, and the flag beyond it always stays clear.
 *
 * Written as a single-level `calc((100% - Npx) * f)`. A nested
 * `calc(calc(100% - Npx) * f)` is valid CSS that browsers accept, but it is one more
 * thing jsdom drops on the floor, and it reads worse.
 */
export function fillWidth(percent: number | null | undefined): string {
  const fraction = percent == null ? 0 : clampPct(percent) / 100;
  return `calc((100% - ${FLAG_LANE}px) * ${fraction})`;
}

/**
 * The mascot's left edge: centered on the fill head, then clamped to stay inside the
 * TRACK. The upper bound is what keeps it out of the flag's column at 100%+, and the
 * lower bound is what keeps it from hanging off the card's left edge at 0%.
 */
export function mascotLeft(percent: number | null | undefined): string {
  const fraction = percent == null ? 0 : clampPct(percent) / 100;
  return `clamp(0px, calc((100% - ${FLAG_LANE}px) * ${fraction} - ${MASCOT_SIZE / 2}px), calc(100% - ${
    FLAG_LANE + MASCOT_SIZE
  }px))`;
}
